import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import {
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';

const EventTimingContext = createContext(null);

export function useEventTiming() {
  const context = useContext(EventTimingContext);
  if (!context) {
    throw new Error('useEventTiming must be used within an EventTimingProvider');
  }
  return context;
}

export function EventTimingProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTiming, setEditingTiming] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Shared form state
  const [selectedScheduleId, setSelectedScheduleId] = useState('');
  const [minutesPostWarmup, setMinutesPostWarmup] = useState('1:00');
  const [addForPanelSwap, setAddForPanelSwap] = useState('1:00');
  const [useSoloForCouples, setUseSoloForCouples] = useState(true);
  const [maxSkatersWarmupCadetSenior, setMaxSkatersWarmupCadetSenior] = useState(5);
  const [maxSkatersWarmupOther, setMaxSkatersWarmupOther] = useState(6);

  // Timing lists by event type
  const [timingsLists, setTimingsLists] = useState({
    freedance: [],
    styledance: [],
    freeskating: [],
    'compulsory-dance': [],
    figures: [],
    teams: [],
  });

  // Competition Breaks (global)
  const [competitionBreaks, setCompetitionBreaks] = useState([]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  // Fetch current user
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setCurrentUser(res.data);
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    };
    fetchCurrentUser();
  }, []);

  // Fetch schedules
  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        const res = await axios.get(`${API}/content/schedules`, { withCredentials: true });
        setSchedules(res.data || []);
      } catch (error) {
        console.error('Error fetching schedules:', error);
      }
    };
    fetchSchedules();
  }, []);

  // Fetch all timings and breaks on mount
  useEffect(() => {
    fetchAllTimings();
    fetchBreaks();
  }, []);

  const fetchAllTimings = async () => {
    const eventTypes = ['freedance', 'styledance', 'freeskating', 'compulsory-dance', 'figures', 'teams'];
    for (const eventType of eventTypes) {
      await fetchTimings(eventType);
    }
  };

  const fetchTimings = async (eventType) => {
    try {
      const res = await axios.get(`${API}/event-timings?event_type=${eventType}`, { withCredentials: true });
      setTimingsLists(prev => ({
        ...prev,
        [eventType]: res.data || []
      }));
    } catch (error) {
      console.error(`Error fetching ${eventType} timings:`, error);
    }
  };

  const fetchBreaks = async () => {
    try {
      const res = await axios.get(`${API}/competition-breaks`, { withCredentials: true });
      setCompetitionBreaks(res.data || []);
    } catch (error) {
      console.error('Error fetching competition breaks:', error);
    }
  };

  const isSuperUser = currentUser?.role === 'super';
  const currentUserId = currentUser?.user_id;

  // Check if user can edit a specific timing
  const canEditTiming = (timing) => {
    if (isSuperUser) return true;
    const schedule = schedules.find(s => s.schedule_id === timing.schedule_id);
    if (schedule?.superuser_only) return false;
    return timing.created_by === currentUserId || schedule?.created_by === currentUserId;
  };

  // Reset form state
  const resetForm = () => {
    setSelectedScheduleId('');
    setMinutesPostWarmup('1:00');
    setAddForPanelSwap('1:00');
    setUseSoloForCouples(true);
    setMaxSkatersWarmupCadetSenior(5);
    setMaxSkatersWarmupOther(6);
    setEditingTiming(null);
  };

  // Handle delete click
  const handleDeleteClick = (timing) => {
    setItemToDelete(timing);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/event-timings/${itemToDelete.timing_id}`, { withCredentials: true });
      toast.success('Timing deleted successfully');
      fetchTimings(itemToDelete.event_type);
    } catch (error) {
      console.error('Error deleting timing:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete timing');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  // Handle drag end for reordering
  const handleDragEnd = async (event, eventType) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const timings = timingsLists[eventType] || [];
    const oldIndex = timings.findIndex(t => t.timing_id === active.id);
    const newIndex = timings.findIndex(t => t.timing_id === over.id);
    
    const reordered = arrayMove(timings, oldIndex, newIndex);
    setTimingsLists(prev => ({
      ...prev,
      [eventType]: reordered
    }));
  };

  // Calculate warmup total from programme time + add for warmup
  const calculateWarmupTotal = (programmeTime, addForWarmup) => {
    if (!programmeTime || !addForWarmup) return '';
    
    const baseMatch = programmeTime.match(/^(\d+):(\d+)/);
    if (!baseMatch) return addForWarmup;
    
    const baseMinutes = parseInt(baseMatch[1]);
    const baseSeconds = parseInt(baseMatch[2]);
    
    const addMatch = addForWarmup.match(/^(\d+):(\d+)/);
    if (!addMatch) return programmeTime;
    
    const addMinutes = parseInt(addMatch[1]);
    const addSeconds = parseInt(addMatch[2]);
    
    let totalSeconds = baseSeconds + addSeconds;
    let totalMinutes = baseMinutes + addMinutes + Math.floor(totalSeconds / 60);
    totalSeconds = totalSeconds % 60;
    
    return `${totalMinutes}:${totalSeconds.toString().padStart(2, '0')}`;
  };

  // Format time input on blur
  const formatTimeOnBlur = (value, setter) => {
    if (!value) return;
    if (!value.includes(':')) {
      setter(`${value}:00`);
    }
  };

  // Check if we should show a section break HR
  const shouldShowHR = (grades, index) => {
    if (index === 0) return false;
    const prev = grades[index - 1];
    const curr = grades[index];
    if (prev.discipline_section !== curr.discipline_section) return true;
    if (prev.classification_section !== curr.classification_section) return true;
    return false;
  };

  const value = {
    // User
    currentUser,
    isSuperUser,
    currentUserId,
    
    // Schedules
    schedules,
    
    // Dialog state
    dialogOpen,
    setDialogOpen,
    editingTiming,
    setEditingTiming,
    saving,
    setSaving,
    
    // Delete dialog
    deleteDialogOpen,
    setDeleteDialogOpen,
    itemToDelete,
    setItemToDelete,
    deleting,
    handleDeleteClick,
    handleConfirmDelete,
    
    // Form state
    selectedScheduleId,
    setSelectedScheduleId,
    minutesPostWarmup,
    setMinutesPostWarmup,
    addForPanelSwap,
    setAddForPanelSwap,
    useSoloForCouples,
    setUseSoloForCouples,
    maxSkatersWarmupCadetSenior,
    setMaxSkatersWarmupCadetSenior,
    maxSkatersWarmupOther,
    setMaxSkatersWarmupOther,
    
    // Timings lists
    timingsLists,
    setTimingsLists,
    fetchTimings,
    
    // Competition Breaks
    competitionBreaks,
    setCompetitionBreaks,
    fetchBreaks,
    
    // DnD
    sensors,
    handleDragEnd,
    
    // Helpers
    canEditTiming,
    resetForm,
    calculateWarmupTotal,
    formatTimeOnBlur,
    shouldShowHR,
  };

  return (
    <EventTimingContext.Provider value={value}>
      {children}
    </EventTimingContext.Provider>
  );
}
